import React from "react";
import { Modal, Button, Spinner } from "reactstrap";

import { fmtData, fmtNumero, motivoLabel } from "./relatorioPdf";
import { rotuloDocumento } from "../../../../utils/modeloDocumento";

const renderLocation = (uf, cidade) =>
  [uf, cidade].filter(Boolean).join(" - ") || "-";

const EXPLANATION_ITEMS = [
  {
    label: "Distância",
    description: "Quilometragem usada no cálculo da rota deste CTe.",
  },
  {
    label: "Peso",
    description: "Carga convertida para toneladas a partir do documento.",
  },
  {
    label: "Fator",
    description: "Índice de emissão do modal em kg CO₂ por ton·km.",
  },
];

const SOURCE_LEGEND = [
  {
    tone: "info",
    label: "Cache",
    description: "Distância reaproveitada de uma rota já conhecida.",
  },
  {
    tone: "success",
    label: "ORS",
    description: "Distância consultada no provedor de rotas.",
  },
  {
    tone: "warning",
    label: "Haversine",
    description: "Estimativa geodésica usada como fallback.",
  },
  {
    tone: "warning",
    label: "Pendente",
    description: "Ainda falta dado ou provedor para concluir o cálculo.",
  },
  {
    tone: "danger",
    label: "Sem origem/peso/fator",
    description: "O CTe não tem informação suficiente para calcular.",
  },
  {
    tone: "danger",
    label: "Erro",
    description: "Ocorreu uma falha técnica no processamento.",
  },
];

const DetailField = ({ label, value }) => (
  <div className="emissoes-detail-field">
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

function DetalheEmissaoModal({
  isOpen,
  onClose,
  loading,
  error,
  detalhe,
  onDownloadPdf,
  pdfLoading,
}) {
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      modalClassName="modal-align-top emissoes-detail-modal"
      fade={false}
    >
      <div className="emissoes-detail-modal__header">
        <div>
          <h4>{rotuloDocumento(detalhe?.modelo)} {detalhe?.numeroCte || detalhe?.idCte || "-"}</h4>
          {detalhe?.chaveAcesso ? (
            <small>Chave: {detalhe.chaveAcesso}</small>
          ) : (
            <small>Detalhes do cálculo de emissão</small>
          )}
        </div>
        <button
          type="button"
          className="emissoes-detail-modal__close"
          onClick={onClose}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <div className="emissoes-detail-modal__body">
        {loading ? (
          <div className="emissoes-detail-modal__loading">
            <Spinner color="info" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="emissoes-detail-modal__empty">
            <strong>Erro:</strong> {error}
          </div>
        ) : null}

        {!loading && !error && detalhe ? (
          <>
            <div className="emissoes-detail-modal__grid">
              <DetailField label="Embarcador" value={detalhe.embarcador} />
              <DetailField label="Transportadora" value={detalhe.transportadora} />
              <DetailField label="Data de emissão" value={fmtData(detalhe.dataEmissao)} />
              <DetailField
                label="Origem"
                value={renderLocation(detalhe.ufOrigem, detalhe.cidadeOrigem)}
              />
              <DetailField
                label="Destino"
                value={renderLocation(detalhe.ufDestino, detalhe.cidadeDestino)}
              />
              <DetailField label="Modal" value={detalhe.modal} />
            </div>

            <div className="emissoes-detail-modal__panel">
              <div className="emissoes-detail-modal__panel-title">
                Cálculo de emissão
              </div>

              {detalhe.calculado && detalhe.emissao ? (
                <>
                  <div className="emissoes-detail-modal__grid emissoes-detail-modal__grid--metrics">
                    <DetailField
                      label="Distância (km)"
                      value={fmtNumero(detalhe.emissao.distanciaKm)}
                    />
                    <DetailField
                      label="Peso (ton)"
                      value={fmtNumero(detalhe.emissao.pesoToneladas, 3)}
                    />
                    <DetailField
                      label="Fator (kg CO₂/ton·km)"
                      value={fmtNumero(detalhe.emissao.fatorUsado, 3)}
                    />
                    <DetailField
                      label="Emissão calculada"
                      value={`${fmtNumero(detalhe.emissao.emissaoKgCO2)} kg CO₂`}
                    />
                    <DetailField
                      label="Fonte da distância"
                      value={detalhe.emissao.fonteDistancia || "-"}
                    />
                    <DetailField
                      label="Calculado em"
                      value={fmtData(detalhe.emissao.calculadoEm)}
                    />
                  </div>

                  <div className="emissoes-detail-modal__formula">
                    <span>Fórmula</span>
                    <strong>distância (km) × peso (ton) × fator</strong>
                    <p>
                      {fmtNumero(detalhe.emissao.distanciaKm)} ×{" "}
                      {fmtNumero(detalhe.emissao.pesoToneladas, 3)} ×{" "}
                      {fmtNumero(detalhe.emissao.fatorUsado, 3)} ={" "}
                      {fmtNumero(detalhe.emissao.emissaoKgCO2)} kg CO₂
                    </p>
                  </div>
                </>
              ) : (
                <div className="emissoes-detail-modal__pending">
                  <strong>Pendente de cálculo</strong>
                  <p>Motivo: {motivoLabel(detalhe.pendencia)}</p>
                  {detalhe.cancelado ? (
                    <small>Observação: CTe está cancelado ou inativo.</small>
                  ) : null}
                </div>
              )}
            </div>

            <div className="emissoes-detail-modal__legend">
              <div className="emissoes-detail-modal__legend-block">
                <div className="emissoes-detail-modal__legend-title">
                  Como interpretar
                </div>
                <div className="emissoes-detail-modal__legend-grid">
                  {EXPLANATION_ITEMS.map((item) => (
                    <div key={item.label} className="emissoes-detail-modal__legend-card">
                      <strong>{item.label}</strong>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="emissoes-detail-modal__legend-block">
                <div className="emissoes-detail-modal__legend-title">
                  Legenda da fonte do cálculo
                </div>
                <div className="emissoes-detail-modal__legend-list">
                  {SOURCE_LEGEND.map((item) => (
                    <div key={item.label} className="emissoes-detail-modal__legend-item">
                      <span
                        className={`emissoes-source-pill emissoes-source-pill--${item.tone}`}
                      >
                        {item.label}
                      </span>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <div className="emissoes-detail-modal__footer">
        <Button className="emissoes-button-secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button
          className="emissoes-button-primary"
          onClick={onDownloadPdf}
          disabled={!detalhe || loading || pdfLoading}
        >
          {pdfLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <i className="tim-icons icon-cloud-download-93" /> Baixar PDF
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
}

export default DetalheEmissaoModal;
